import React from "react";
import { Link } from "react-router-dom";
import {
  FacebookLogo,
  TwitterLogo,
  YoutubeLogo,
  LinkedinLogo,
  ArrowUpRight,
  MapPin,
  Phone,
  EnvelopeSimple,
} from "@phosphor-icons/react";

const FOOTER_LINKS = {
  Company: [
    { label: "About Us", to: "/about" },
    { label: "Services", to: "/services" },
    { label: "Industries", to: "/industries" },
    { label: "Contact", to: "/contact" },
  ],
  Services: [
    { label: "Petroleum Trading", to: "/services" },
    { label: "Shipping & Logistics", to: "/services" },
    { label: "Pipeline Engineering", to: "/services" },
    { label: "Natural Gas & Power", to: "/services" },
    { label: "Upstream Support", to: "/services" },
  ],
};

const Footer = () => {
  return (
    <footer
      className="border-t border-border bg-card text-card-foreground"
      data-testid="site-footer"
    >
      {/* Big CTA strip */}
      <div className="container py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="overline text-primary mb-4">— Let's build</div>
            <h2 className="font-display font-bold tracking-tight text-4xl md:text-6xl lg:text-7xl leading-[0.95]">
              Power the future
              <br />
              <span className="text-primary">together.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="inline-flex items-center gap-3 group"
            >
              <span className="grid place-items-center w-16 h-16 rounded-full bg-primary text-primary-foreground transition-transform group-hover:scale-110">
                <ArrowUpRight size={28} weight="bold" />
              </span>
              <span className="font-display text-xl">Start a project</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-border/70">
        <div className="container py-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <span className="grid place-items-center w-9 h-9 rounded-sm bg-primary text-primary-foreground font-display font-black text-lg">
                F
              </span>
              <div className="leading-tight">
                <div className="font-display font-bold text-lg">FRADRO</div>
                <div className="overline text-muted-foreground -mt-0.5">
                  Group
                </div>
              </div>
            </Link>
            <p className="text-muted-foreground max-w-md leading-relaxed">
              A diversified energy conglomerate delivering integrated solutions
              across petroleum trading, pipeline engineering, natural gas,
              shipping & logistics, and upstream support — across Nigeria and
              the wider African market.
            </p>

            <ul className="mt-8 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-primary mt-0.5 shrink-0" />
                <span>
                  11th Floor, Foreshore Towers, 2a Osborne Road, Ikoyi,
                  Lagos, Nigeria
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-primary shrink-0" />
                <a href="tel:+2348099992485" className="hover:text-primary">
                  +234 809 999 2485
                </a>
              </li>
              <li className="flex items-center gap-3">
                <EnvelopeSimple size={18} className="text-primary shrink-0" />
                <a
                  href="mailto:info@fradrogroup.com"
                  className="hover:text-primary"
                >
                  info@fradrogroup.com
                </a>
              </li>
            </ul>
          </div>

          {Object.entries(FOOTER_LINKS).map(([title, links]) => (
            <div key={title} className="lg:col-span-3">
              <div className="overline text-muted-foreground mb-5">
                {title}
              </div>
              <ul className="space-y-3">
                {links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-foreground/80 hover:text-primary transition-colors text-sm"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-1 lg:justify-self-end">
            <div className="overline text-muted-foreground mb-5">Social</div>
            <div className="flex lg:flex-col gap-3">
              {[
                { Icon: LinkedinLogo, href: "#" },
                { Icon: TwitterLogo, href: "#" },
                { Icon: FacebookLogo, href: "#" },
                { Icon: YoutubeLogo, href: "#" },
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  className="grid place-items-center w-10 h-10 rounded-full border border-border hover:border-primary hover:text-primary transition-colors"
                  aria-label="social"
                >
                  <Icon size={16} weight="bold" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border/70">
        <div className="container py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>
            © {new Date().getFullYear()} Fradro Group. All rights reserved.
          </div>
          <div className="font-mono uppercase tracking-widest">
            Lagos · Port Harcourt · Warri · Kaduna
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
